import React, { useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as MicrophoneIcon } from '../../assets/svg/microphone-solid.svg';
import { useSpeechToText, useMicrophonePermission, useIsMobile } from './hooks';
import { useDialogManager } from '../dialogs/Dialogs';
import { useZakeke } from '@zakeke/zakeke-configurator-react';

const WarningMessageDialog: React.FC<{
	title?: string;
	message: string;
	onClose: () => void;
	align?: string;
}> = ({ title, message, onClose, align }) => (
	<div style={{ padding: '20px', textAlign: align === 'center' ? 'center' : 'left' }}>
		{title && <h3 style={{ marginBottom: '12px' }}>{title}</h3>}
		<p>{message}</p>
		<button onClick={onClose} style={{ marginTop: '16px', padding: '8px 16px', cursor: 'pointer' }}>
			OK
		</button>
	</div>
);

interface PromptInputProps {
	prompt: string;
	onPromptChange: (value: string) => void;
	onVoiceInput?: () => void;
	placeholder?: string;
	disabled?: boolean;
}

export const PromptInput: React.FC<PromptInputProps> = ({
	prompt,
	onPromptChange,
	onVoiceInput,
	placeholder,
	disabled = false,
}) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const isMobile = useIsMobile();
	const maxLength = 500;
	const currentLength = prompt.length;

	const { speechApiAvailable, transcript, isListening, startListening, stopListening } = useSpeechToText();
	const { permissionState: micPermission, featurePolicyMicrophone } = useMicrophonePermission();
	const { showDialog, closeDialog } = useDialogManager();
	const lastTranscriptRef = useRef('');
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		const textarea = textareaRef.current;
		if (textarea) {
			textarea.style.height = 'auto';
			const newHeight = Math.min(textarea.scrollHeight, 200);
			textarea.style.height = `${newHeight}px`;
		}
	}, [prompt]);

	const showRecordButton =
		speechApiAvailable &&
		(!(!featurePolicyMicrophone && micPermission === 'denied') ||
			(featurePolicyMicrophone && featurePolicyMicrophone === true && micPermission === 'denied'));

	useEffect(() => {
		if (transcript && transcript !== lastTranscriptRef.current) {
			const truncatedTranscript = transcript.slice(0, maxLength);
			onPromptChange(truncatedTranscript);
			lastTranscriptRef.current = transcript;
		}
	}, [transcript, onPromptChange, maxLength]);

	const handleVoiceButtonClick = () => {
		if (featurePolicyMicrophone === true && micPermission === 'denied') {
			showDialog(
				'enable-microphone',
				<WarningMessageDialog
					title={T._('Microphone permission blocked')}
					message={T._(
						'The microphone permission is blocked. Please enable it in your browser settings to use speech-to-text functionality.'
					)}
					onClose={() => closeDialog('enable-microphone')}
					align='center'
				/>
			);
		} else {
			if (isListening) {
				stopListening();
			} else {
				startListening(prompt);
			}
		}
		onVoiceInput?.();
	};

	return (
		<PromptWrapper>
			<PromptArea>
				{isMobile ? (
					<StyledPromptTextInput
						type='text'
						placeholder={placeholder || T._('Describe your scene (optional)...')}
						value={prompt}
						onChange={(e) => onPromptChange(e.target.value)}
						disabled={disabled || isListening}
						maxLength={maxLength}
						aria-label={T._('Describe your scene (optional)...')}
					/>
				) : (
					<StyledPromptInput
						ref={textareaRef}
						placeholder={placeholder || T._('Describe your scene (optional)...')}
						value={prompt}
						onChange={(e) => onPromptChange(e.target.value)}
						disabled={disabled || isListening}
						maxLength={maxLength}
						rows={1}
						aria-label={T._('Describe your scene (optional)...')}
					/>
				)}
				{showRecordButton && (
					<VoiceButton
						title={isListening ? T._('Stop recording') : T._('Voice input')}
						onClick={handleVoiceButtonClick}
						disabled={disabled}
						$recording={isListening}
						aria-label={isListening ? T._('Stop recording') : T._('Start voice input')}
					>
						{isListening ? (
							<RecordingIndicator aria-hidden='true'>●</RecordingIndicator>
						) : (
							<MicrophoneIcon aria-hidden='true' />
						)}
					</VoiceButton>
				)}
			</PromptArea>
			<CharacterCounter isNearLimit={currentLength > maxLength * 0.9}>
				{currentLength}/{maxLength}
			</CharacterCounter>
		</PromptWrapper>
	);
};

const PromptWrapper = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

const PromptArea = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	background-color: white;
	border-radius: 0.75rem;
	padding: 0.625rem 1rem;
	border: 1px solid #d1d5db;
	transition: all 0.2s;

	&:focus-within {
		border-color: #c084fc;
		box-shadow: 0 0 0 3px rgba(192, 132, 252, 0.1);
	}
`;

const StyledPromptInput = styled.textarea`
	flex: 1;
	background: transparent;
	outline: none;
	font-size: 0.875rem;
	color: #374151;
	border: none;
	resize: none;
	overflow-y: auto;
	min-height: 1.5rem;
	max-height: 120px;
	line-height: 1.5;
	font-family: inherit;

	&::placeholder {
		color: #9ca3af;
	}
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const StyledPromptTextInput = styled.input`
	flex: 1;
	background: transparent;
	outline: none;
	font-size: 0.875rem;
	color: #374151;
	border: none;
	line-height: 1.5;
	font-family: inherit;
	height: 1.5rem;
	max-width: calc(100vw - 210px);

	&::placeholder {
		color: #9ca3af;
	}
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const VoiceButton = styled.button<{ $recording?: boolean }>`
	width: 2rem;
	height: 2rem;
	border-radius: 9999px;
	background-color: ${(props) => (props.$recording ? '#ef4444' : '#f3f4f6')};
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	flex-shrink: 0;
	border: none;
	cursor: pointer;
	animation: ${(props) => (props.$recording ? 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none')};

	svg {
		width: 1rem;
		height: 1rem;
		color: #6b7280;
	}

	&:hover:not(:disabled) {
		background-color: ${(props) => (props.$recording ? '#dc2626' : '#e5e7eb')};
	}
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.7;
		}
	}
`;

const RecordingIndicator = styled.span`
	color: white;
	font-size: 1.25rem;
	line-height: 1;
`;

const CharacterCounter = styled.div<{ isNearLimit: boolean }>`
	display: none;
	font-size: 0.75rem;
	color: ${(props) => (props.isNearLimit ? '#dc2626' : '#6b7280')};
	text-align: right;
	margin-top: 0.25rem;
	padding: 0 0.75rem;
	transition: color 0.2s;
`;
